import * as Sentry from '@sentry/vue'
import { singletonInstanceSummoner } from '../../../common/singletonInstanceSummoner'

const hookNameForOverrideErrorReport = '$CORE.$errorReporter.reportOverride'

/**
 * # $core.$errorReporter
 *
 * - SentryにFrontエラーを送信するサービス。 (Sentryのライブラリを利用)
 * - 各サービス, 関数内で try ... catch でエラーをキャッチして、ここに送信する。
 * - production環境の場合のみ、Sentryにエラーが送信されます。
 *
 * ## report
 * 利用例:
 * ```ts
 * try {
 *   // 何かの処理
 * } catch (e) {
 *   console.error(e)
 *   // Sentryにエラーを送信
 *   $core.$errorReporter.r(e, this)
 * }
 * ```
 */
class ErrorReporter {
  sentryInstance: typeof Sentry = Sentry
  async report(e: any, callerInstance: any, displayMessage: string = null) {
    try {
      if ($core.$appHook.hasHook(hookNameForOverrideErrorReport)) {
        $core.$appHook.emit(hookNameForOverrideErrorReport, {
          error: e,
          callerInstance,
          errorReporterInstance: this,
        })
        return
      }
      // NoNeedReportErrorであれば、何もしない
      if (e?.name === 'NoNeedReportError') {
        return
      }
      const callerInstanceName =
        callerInstance?.constructor?.name ||
        callerInstance?.name ||
        callerInstance?.__name ||
        callerInstance?.$options?.name ||
        callerInstance?.$options?._componentTag
      const errorMessage = [
        '[ErrorReporter]',
        displayMessage || '',
        callerInstanceName ? `in [${callerInstanceName}]` : '',
        e.message,
      ]
        .filter(Boolean)
        .join(' ')
      console.error(errorMessage, {
        e,
        callerInstance,
      })
      const isVueError = !!callerInstance?.$options
      if ($core.isProduction) {
        this.sentryInstance.captureException(e, callerInstance)
      }
      // Adminのみエラー表示
      if ($core.$embAuth?.user?.isAdmin) {
        $core.$toast.errorToast(errorMessage)
      }
    } catch (e) {
      // Loop を回避
      console.error(e)
    }
  }

  async r(e: any, callerInstance: any, displayMessage: string = null) {
    this.report(e, callerInstance, displayMessage)
  }

  static get instance(): ErrorReporter {
    return singletonInstanceSummoner('ErrorReporter', ErrorReporter)
  }
}

export const $errorReporter = ErrorReporter.instance
